import React, {useState} from 'react';
import {Autocomplete, TextField} from '@mui/material';
import {useTheme} from "../../../contexts/theme";
import {styled} from '@mui/material/styles';
import Popper from '@mui/material/Popper';

interface StyledAutoCompleteProps {
    name: string;
    label: string | undefined;
    options: any[]; // Assuming options are an array of strings
    value?: any[]; // For non-react-hook-form usage, use this to control the value
    onChange?: (value: any, newValue: any) => void; // Callback for handling change in non-react-hook-form
    backgroundColor?: string;
    customAutocomplete?: boolean;
    textFieldValue?: string;
    textFieldOnChange?: (value: string) => void;
    labelField?: string;
    valueField?: string;
    freeSolo?: boolean;
}

const MaMStyledAutoComplete: React.FC<StyledAutoCompleteProps> = ({
                                                                   customAutocomplete,
                                                                   name,
                                                                   label,
                                                                   options,
                                                                   value,
                                                                   onChange,
                                                                   backgroundColor = undefined,
                                                                   textFieldValue,
                                                                   textFieldOnChange = () => {},
    valueField,
    labelField,
                                                                      freeSolo=false
                                                               }) => {
    const theme = useTheme();
    const [open, setOpen] = useState(false); // Control the opening state of the options list
    const limitOptionsNumber = 5;

    // Custom Popper component
    const CustomPopper = styled(Popper)(() => ({
        '& .MuiAutocomplete-listbox': {
            backgroundColor: theme.palette.blue,
            color: theme.palette.white,
        },
        '& .MuiAutocomplete-option': {
            '&[aria-selected="true"]': {
                backgroundColor: theme.palette.blue,
            },
            '&:hover': {
                backgroundColor: theme.palette.blue,
            },
        },
        // Style for the "No options" message
        '& .MuiAutocomplete-noOptions': {
            backgroundColor: theme.palette.blue, // Change this to your desired background color
            color: theme.palette.white,          // Adjust the text color if necessary
            padding: '10px',                     // Optional: add padding for better appearance
        },
    }));

    const handleInputChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const newValue = event.target.value;
        setOpen(newValue.length > 0);
        textFieldOnChange(newValue);
    };

    const handleValueChange = (event: any, newValue: any) => {
        if (onChange) {
            onChange(value, newValue);
        }
        setOpen(false);
        textFieldOnChange('')
    };

    // Custom filter options function
    const customFilterOptions = (localOptions: any[], state: { inputValue: string }) => {
        return localOptions
            .filter((option: any) => labelField ? option[labelField].toLowerCase().includes(state.inputValue.toLowerCase()) :
                option.toLowerCase().includes(state.inputValue.toLowerCase())
            )
    };

    return (
        <Autocomplete
            multiple
            freeSolo={freeSolo}
            limitTags={1}
            options={options}
            filterSelectedOptions
            onChange={customAutocomplete ? handleValueChange : onChange}
            value={value}
            inputValue={textFieldValue}
            open={customAutocomplete && open}
            PopperComponent={customAutocomplete ? CustomPopper : undefined}
            filterOptions={customAutocomplete ? customFilterOptions : undefined}
            getOptionLabel={(option: any) => labelField ? option[labelField] : option}
            getOptionKey={(option: any) => valueField ? option[valueField] : option}
            isOptionEqualToValue={(option, value) => valueField ? option[valueField] === value[valueField] : option === value}
            sx={{
                "& .MuiAutocomplete-popupIndicator": {
                    color: theme.palette.lightGrey,
                },
                "& .MuiAutocomplete-clearIndicator": {
                    color: theme.palette.lightGrey,
                },
            }}
            renderInput={(params) => (
                <TextField
                    sx={{
                        backgroundColor: backgroundColor,
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: theme.palette.white, // Default border color
                            },
                            '&:hover fieldset': {
                                borderColor: theme.palette.orange, // Border color on hover
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.orange, // Border color when focused (orange)
                            },
                            '& .MuiInputBase-input': {  // Target the input text color
                                color: theme.palette.white,  // Ensures input text is white
                            },
                            '& .MuiChip-root': {
                                color: theme.palette.blue,
                                backgroundColor: theme.palette.orange,
                            },
                        },
                        '& .MuiFormLabel-root': {
                            color: theme.palette.white,
                        },
                        '& .MuiFormLabel-root.Mui-focused': {
                            color: theme.palette.orange, // Label color when focused (orange)
                        },
                        // Placeholder styling
                        '& .MuiInputBase-input::placeholder': {
                            color: theme.palette.white,
                        },
                    }}
                    {...params}
                    label={label}
                    placeholder={label}
                    onChange={customAutocomplete ? handleInputChange : () => {}}
                />
            )}
        />
    );
};

export default MaMStyledAutoComplete;
